import React, { useContext } from "react";
import ReactMarkdown from "react-markdown";
import styled from "@emotion/styled";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Chip,
  Paper,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { DateTime } from "luxon";

import {
  type CodeReviewFinding,
  type FailureCodeReview,
  type ReviewIteration,
  type RunData,
} from "../../utils/types";
import { AuthContext } from "@/auth/AuthContext";

interface ReviewIterationsTabProps {
  display: boolean;
  iterations: ReviewIteration[];
  finalClassification?: RunData["finalFailureClassification"];
  reviewSystemError?: string | null;
  failureCodeReview?: FailureCodeReview;
}

const ReviewIterationsTab = ({
  display,
  iterations,
  finalClassification,
  reviewSystemError,
  failureCodeReview,
}: ReviewIterationsTabProps) => {
  const { user } = useContext<any>(AuthContext);
  const isSuperUser = user?.role === "superUser";
  if (!display) return null;

  return (
    <TabContent>
      <SummaryBanner
        finalClassification={finalClassification}
        reviewSystemError={reviewSystemError}
      />
      {/* Superusers see the code review while it's still a proposal; once the PR
          is created (prUrl set) it becomes visible to the org's users too. */}
      {(isSuperUser || !!failureCodeReview?.prUrl) && (
        <CodeReviewSection failureCodeReview={failureCodeReview} />
      )}
      {iterations.map((iteration) => (
        <IterationCard
          key={`${iteration.iteration}-${iteration.attempted_at}`}
          iteration={iteration}
        />
      ))}
    </TabContent>
  );
};

const SEVERITY_CHIP: Record<
  CodeReviewFinding["severity"],
  { color: "default" | "info" | "warning" | "error"; label: string }
> = {
  low: { color: "default", label: "Low" },
  medium: { color: "info", label: "Medium" },
  high: { color: "warning", label: "High" },
  critical: { color: "error", label: "Critical" },
};

const CodeReviewSection = ({
  failureCodeReview,
}: {
  failureCodeReview?: FailureCodeReview;
}) => {
  if (!failureCodeReview) return null;

  if (failureCodeReview.status === "error") {
    return (
      <Alert severity="warning">
        <AlertTitle>Code review didn't complete</AlertTitle>
        {failureCodeReview.error ||
          "The repository code review failed to run for this failure."}
      </Alert>
    );
  }

  const findings = failureCodeReview.findings ?? [];
  const repoLabel = [
    failureCodeReview.repo,
    failureCodeReview.commitSha
      ? `@${failureCodeReview.commitSha.slice(0, 7)}`
      : null,
  ]
    .filter(Boolean)
    .join("");

  return (
    <CodeReviewPaper elevation={2}>
      <CardHeader>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Code review
        </Typography>
        <Chip
          size="small"
          color={findings.length ? "error" : "success"}
          label={
            findings.length
              ? `${findings.length} issue${findings.length === 1 ? "" : "s"}`
              : "No issues found"
          }
        />
        {repoLabel && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {repoLabel}
          </Typography>
        )}
        {failureCodeReview.prUrl && (
          <a
            href={failureCodeReview.prUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#4ea3ff", textDecoration: "underline", fontSize: "0.875rem" }}
          >
            View PR
          </a>
        )}
        {!failureCodeReview.prUrl && failureCodeReview.commentUrl && (
          <a
            href={failureCodeReview.commentUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#4ea3ff", textDecoration: "underline", fontSize: "0.875rem" }}
          >
            View PR comment
          </a>
        )}
      </CardHeader>

      {failureCodeReview.truncated && (
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
          The review hit its budget before finishing — findings may be partial.
        </Typography>
      )}

      {findings.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          The agent reviewed the repository and found no code issues tied to this
          failure.
        </Typography>
      ) : (
        findings.map((finding, idx) => (
          <FindingCard key={idx} finding={finding} />
        ))
      )}
    </CodeReviewPaper>
  );
};

const FindingCard = ({ finding }: { finding: CodeReviewFinding }) => {
  const severity =
    SEVERITY_CHIP[finding.severity] ?? SEVERITY_CHIP.medium;
  const location =
    finding.filePath +
    (finding.lineStart
      ? `:${finding.lineStart}${
          finding.lineEnd && finding.lineEnd !== finding.lineStart
            ? `-${finding.lineEnd}`
            : ""
        }`
      : "");

  return (
    <Accordion disableGutters elevation={0} sx={{ background: "transparent" }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          <Chip size="small" color={severity.color} label={severity.label} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {finding.title}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Section>
          <SectionLabel>Location</SectionLabel>
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {location}
          </Typography>
        </Section>
        {finding.description && (
          <Section>
            <SectionLabel>What goes wrong</SectionLabel>
            <MarkdownContainer>
              <ReactMarkdown>{finding.description}</ReactMarkdown>
            </MarkdownContainer>
          </Section>
        )}
        {finding.rationale && (
          <Section>
            <SectionLabel>Why it explains the failure</SectionLabel>
            <MarkdownContainer>
              <ReactMarkdown>{finding.rationale}</ReactMarkdown>
            </MarkdownContainer>
          </Section>
        )}
        {finding.suggestedImprovement && (
          <Section>
            <SectionLabel>Suggested improvement</SectionLabel>
            <MarkdownContainer>
              <ReactMarkdown>{finding.suggestedImprovement}</ReactMarkdown>
            </MarkdownContainer>
          </Section>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const SummaryBanner = ({
  finalClassification,
  reviewSystemError,
}: {
  finalClassification?: RunData["finalFailureClassification"];
  reviewSystemError?: string | null;
}) => {
  if (reviewSystemError) {
    return (
      <Alert severity="error">
        <AlertTitle>Auto-review system error</AlertTitle>
        {reviewSystemError}
      </Alert>
    );
  }
  if (finalClassification === "legitimate_app_issue") {
    return (
      <Alert severity="info">
        <AlertTitle>Auto-classified as app issue</AlertTitle>
        The vision agent reviewed this failure and concluded the app itself
        produced the wrong behavior. No automatic retry was performed.
      </Alert>
    );
  }
  if (finalClassification === "agentic_failure_unresolved") {
    return (
      <Alert severity="warning">
        <AlertTitle>Retried automatically 2× — still unresolved</AlertTitle>
        Each iteration's refined test definition and reasoning is shown below.
      </Alert>
    );
  }
  if (finalClassification === "legitimate_pass") {
    return (
      <Alert severity="success">
        <AlertTitle>Auto-verified pass</AlertTitle>
        The success criteria were verified against the final screenshots.
      </Alert>
    );
  }
  if (finalClassification === "false_pass_unresolved") {
    return (
      <Alert severity="error">
        <AlertTitle>Falsely passed 2× — flipped to failed</AlertTitle>
        Two auto-verification iterations concluded the agent declared success
        without meeting the criteria. The run has been marked as failed.
      </Alert>
    );
  }
  return null;
};

const CLASSIFICATION_CHIP: Record<
  ReviewIteration["classification"],
  { color: "success" | "warning" | "info" | "error"; label: string }
> = {
  legitimate_app_issue: { color: "error", label: "App issue" },
  agent_failure: { color: "warning", label: "Agent failure" },
  legitimate_pass: { color: "info", label: "Legitimate pass" },
  false_positive_pass: { color: "error", label: "False-positive pass" },
};

const IterationCard = ({ iteration }: { iteration: ReviewIteration }) => {
  const chipConfig =
    CLASSIFICATION_CHIP[iteration.classification] ??
    CLASSIFICATION_CHIP.legitimate_app_issue;
  const classificationChip = (
    <Chip size="small" color={chipConfig.color} label={chipConfig.label} />
  );

  const confidenceLabel =
    iteration.confidence != null
      ? `confidence ${iteration.confidence.toFixed(2)}`
      : null;

  const attemptedAt =
    iteration.attempted_at != null
      ? DateTime.fromMillis(iteration.attempted_at).toRelative()
      : null;

  return (
    <IterationPaper elevation={2}>
      <CardHeader>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Iteration {iteration.iteration}
        </Typography>
        {classificationChip}
        {confidenceLabel && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {confidenceLabel}
          </Typography>
        )}
        {attemptedAt && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {attemptedAt}
          </Typography>
        )}
      </CardHeader>

      {iteration.reasoning && (
        <Section>
          <SectionLabel>Reasoning</SectionLabel>
          <MarkdownContainer>
            <ReactMarkdown>{iteration.reasoning}</ReactMarkdown>
          </MarkdownContainer>
        </Section>
      )}

      {iteration.insights && iteration.insights.length > 0 && (
        <Section>
          <SectionLabel>Insights</SectionLabel>
          <ul style={{ listStyle: "disc", paddingLeft: "1.25rem", margin: 0 }}>
            {iteration.insights.map((insight, idx) => (
              <li key={idx}>
                <Typography variant="body2">{insight}</Typography>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {iteration.failure_status_message && (
        <Accordion disableGutters elevation={0} sx={{ background: "transparent" }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <SectionLabel>Original failure message</SectionLabel>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
            >
              {iteration.failure_status_message}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion disableGutters elevation={0} sx={{ background: "transparent" }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <SectionLabel>Refined test task</SectionLabel>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr"
            gap={2}
            sx={{ overflow: "hidden" }}
          >
            <DiffColumn>
              <DiffColumnHeader>Previous</DiffColumnHeader>
              <MarkdownContainer>
                <ReactMarkdown>
                  {iteration.previous_global_test_task || "_(empty)_"}
                </ReactMarkdown>
              </MarkdownContainer>
            </DiffColumn>
            <DiffColumn>
              <DiffColumnHeader>Refined</DiffColumnHeader>
              <MarkdownContainer>
                {iteration.adjusted_global_test_task ? (
                  <ReactMarkdown>
                    {iteration.adjusted_global_test_task}
                  </ReactMarkdown>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No refinement — outcome accepted as-is.
                  </Typography>
                )}
              </MarkdownContainer>
            </DiffColumn>
          </Box>
        </AccordionDetails>
      </Accordion>

      {iteration.video_url_snapshot && (
        <SnapshotLinks>
          <a
            href={iteration.video_url_snapshot}
            target="_blank"
            rel="noopener noreferrer"
          >
            Video of this attempt
          </a>
        </SnapshotLinks>
      )}
    </IterationPaper>
  );
};

const TabContent = styled.div`
  grid-area: tab-content;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  overflow-y: auto;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const Section = styled.div`
  margin-bottom: 12px;
`;

const SectionLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 4px;
`;

const MarkdownContainer = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
  & p {
    margin: 0 0 0.5rem 0;
  }
  & h1,
  & h2,
  & h3,
  & h4 {
    margin: 0.75rem 0 0.25rem 0;
  }
  & ul,
  & ol {
    margin: 0 0 0.5rem 0;
    padding-left: 1.25rem;
  }
  & code {
    background: rgba(255, 255, 255, 0.08);
    padding: 0 4px;
    border-radius: 3px;
  }
`;

const DiffColumn = styled.div`
  min-width: 0;
  overflow-x: auto;
`;

const DiffColumnHeader = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 6px;
`;

const SnapshotLinks = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
  font-size: 0.875rem;
  a {
    color: #4ea3ff;
    text-decoration: underline;
  }
`;

const IterationPaper = styled(Paper)`
  padding: 16px;
  border-radius: 16px;
`;

const CodeReviewPaper = styled(Paper)`
  padding: 16px;
  border-radius: 16px;
`;

export default ReviewIterationsTab;
